<div class="edit-report-wrapper mat-elevation-z1">
  <form [formGroup]="form">
    <h1>Thermal Analysis Report</h1>
    <div class="form-wrapper">

      <!-- Submittal Date -->
      <div class="col-2 b1">
        <mat-label class="mat-label">Submittal Date</mat-label>
        <mat-form-field appearance="outline">
          <input
            class="mat-date-input"
            readonly
            matInput
            formControlName="reportDate"
            [matDatepicker]="picker"
            [min]="today"
            />
            <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
          </mat-form-field>
        </div>

        <!-- Address To -->
        <div class="col-2 online-type b2">
          <mat-label class="mat-label">Address To</mat-label>
          <mat-form-field appearance="outline" class="mat-form-field-width">
            <mat-select disableOptionCentering formControlName="isBilling" placeholder="Select">
              <mat-option [value]="false">Company</mat-option>
              <mat-option [value]="true">Billing</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Main Body -->
        <div class="col-11 b3">
          <mat-label class="mat-label">Main Body Intro</mat-label>
          <div class="c-w">
            <app-richtext-editor formControlName="mainBodyIntro" minHeight="80px"></app-richtext-editor>
          </div>
          <mat-label class="mat-label" style="margin-top: 12px;">Main Body</mat-label>
          <div class="c-w">
            <app-richtext-editor formControlName="mainBody" minHeight="200px"></app-richtext-editor>
          </div>
        </div>

        <!-- Field Test Results Table (only shown for pre-approval reports) -->
        @if (fieldtesting) {
          <div class="col-11 b3">
            <mat-label class="mat-label">Field Test Results</mat-label>
            <div class="custom-table-actions">
              <button type="button" mat-stroked-button (click)="addCustomColumn()">Add Column</button>
              <button type="button" mat-stroked-button (click)="addCustomRow()">Add Row</button>
            </div>
            <div class="custom-table-wrapper">
              <table class="custom-table" [style.minWidth.px]="customTableWidth">
                <colgroup>
                <col class="gutter-col" />
                @for (width of customTable.columnWidths; track $index; let c = $index) {
                  <col
                    [style.width.px]="width"
                    />
                }
              </colgroup>
              <tbody>
                <!-- Control row: drag handle + delete button above each header column -->
                <tr class="control-row">
                  <td class="corner-cell"></td>
                  @for (width of customTable.columnWidths; track $index; let c = $index) {
                    <td
                      class="col-control-cell"
                      [class.col-dragging]="draggedColumn === c"
                      [class.col-drag-over]="dragOverColumn === c && draggedColumn !== c"
                      [draggable]="c !== 0"
                      (dragstart)="onColumnDragStart($event, c)"
                      (dragover)="onColumnDragOver($event, c)"
                      (dragleave)="onColumnDragLeave(c)"
                      (drop)="onColumnDrop($event, c)"
                      (dragend)="onColumnDragEnd()"
                      >
                      <span
                        class="col-drag-handle"
                        [class.col-drag-handle-locked]="c === 0"
                        [title]="c === 0 ? 'Header column cannot be reordered' : 'Drag to reorder column'"
                      >&#8942;&#8942;</span>
                      <button
                        type="button"
                        class="delete-btn"
                        [title]="c === 0 ? 'Header column cannot be deleted' : 'Delete column'"
                        [disabled]="c === 0"
                        (click)="deleteCustomColumn(c)"
                      >&times;</button>
                    </td>
                  }
                </tr>
                @for (row of customTable.rows; track $index; let r = $index) {
                  <tr
                    [style.height.px]="customTable.rowHeights[r]"
                    >
                    <!-- Row-delete column: styled like a regular column -->
                    <td class="row-control-cell" [class.custom-header-cell]="r === 0">
                      <button
                        type="button"
                        class="delete-btn"
                        [title]="r === 0 ? 'Header row cannot be deleted' : 'Delete row'"
                        [disabled]="r === 0"
                        (click)="deleteCustomRow(r)"
                      >&times;</button>
                    </td>
                    <!-- Track by index so editing a cell's value doesn't recreate the input (which would drop focus and jump the cursor) -->
                    @for (cell of row; track $index; let c = $index) {
                      <td
                        class="custom-cell"
                        [class.custom-header-cell]="r === 0"
                        [class.locked-cell]="r === 0 && c === 0"
                        >
                        <input
                          class="editable-cell custom-table-input"
                          type="text"
                          [readonly]="r === 0 && c === 0"
                          [title]="r === 0 && c === 0 ? 'Header column label cannot be edited' : ''"
                          [(ngModel)]="customTable.rows[r][c]"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="markFieldTestResultsDirty()"
                          />
                          @if (r === 0) {
                            <span
                              class="col-resizer"
                              (mousedown)="startColumnResize($event, c)"
                            ></span>
                          }
                          @if (c === 0) {
                            <span
                              class="row-resizer"
                              (mousedown)="startRowResize($event, r)"
                            ></span>
                          }
                        </td>
                      }
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }

        <!-- Native Soil Sample Table -->
        <div class="col-11 b3">
          <mat-label class="mat-label">Native Soil Sample Table</mat-label>
          <div class="redittable-sample-wrapper">
            <table>
              <tr>
                <th rowspan="2" class="sample-id-col"><span>Sample ID</span></th>
                <th rowspan="2" colspan="3" class="depth-col">
                  <span>Depth </span>
                  @if (!metricUnitValue) {
                    <span>(ft)</span>
                  }
                  @if (metricUnitValue) {
                    <span>(m)</span>
                  }
                </th>
                <th rowspan="2" class="effort-col">
                  <span>Effort </span><span>(%) </span>
                </th>
                <th rowspan="2" class="description-col">
                  <span>Description</span>
                  <span class="company-name-wrapper">
                    (<input
                    class="editable-cell company-name-input"
                    type="text"
                    formControlName="companyName"
                    placeholder="Company"
                    />)
                  </span>
                </th>
                <th colspan="2" class="thermal-res">
                  <span>Thermal Resistivity</span>
                  @if (!metricUnitValue) {
                    <span>(°C-cm/W)</span>
                  }
                  @if (metricUnitValue) {
                    <span>(°K.m/W)</span>
                  }
                </th>
                <th rowspan="2" class="mc">
                  <span>Moisture Content</span><span>(%) </span>
                </th>
                <th rowspan="2" class="dry-density">
                  <span>Dry Density</span>
                  @if (!metricUnitValue) {
                    <span>(lb/ft³) </span>
                  }
                  @if (metricUnitValue) {
                    <span>(Kg/m³)</span>
                  }
                </th>
              </tr>
              <tr class="b">
                <th class="c"><span>Wet</span></th>
                <th class="c"><span>Dry</span></th>
              </tr>

              @for (thermalData of thermalDataArray.controls; track thermalData; let i = $index) {
                <tr [formGroup]="$any(thermalData)">
                  <td class="sample-id-cell"><input class="editable-cell" type="text" formControlName="sampleId" /></td>
                  <td class="depth-cell"><input class="editable-cell" type="text" formControlName="startDepth" [readonly]="$safeNavigationMigration(thermalData.get('isStockPile')?.value)" /></td>
                  <td class="depth-cell depth-to-cell">To</td>
                  <td class="depth-cell"><input class="editable-cell" type="text" formControlName="endDepth" [readonly]="$safeNavigationMigration(thermalData.get('isStockPile')?.value)" /></td>
                  @if (previewData?.reportType !== 'Tube') {
                    <td>
                      {{ thermalData.get('effort')?.value }}
                    </td>
                  }
                  <td class="description-cell"><input class="editable-cell" type="text" formControlName="description" /></td>
                  <td>{{ thermalData.get('wet')?.value }}</td>
                  <td>{{ thermalData.get('dry')?.value }}</td>
                  <td>{{ thermalData.get('moistureContent')?.value }}</td>
                  <td>{{ thermalData.get('density')?.value }}</td>
                </tr>
              }
            </table>
          </div>
        </div>
        <br />

        <br />

        <!-- Native Comments -->
        <div
          class="col-11 b3 b4"
          >
          <mat-label class="mat-label">Comments</mat-label>
          <div class="c-w">
            <app-richtext-editor formControlName="commentPart1" minHeight="80px"></app-richtext-editor>
          </div>
        </div>

        <br />

        <!-- Signature -->
        <div class="col-11">
          <mat-label class="mat-label">Signature</mat-label>
          <mat-form-field appearance="outline" class="col-2">
            <mat-select disableOptionCentering formControlName="signatureTypeId" placeholder="Select" [compareWith]="compareSignatureIds">
              <mat-option value="C063AD0D-903F-447B-BCCC-A3C014CFAD7E" class="arrow">Nimesh Patel</mat-option>
              <mat-option value="98FAE02D-953F-4ADB-9794-575A0882A5A3" class="arrow">Deepak Parmar</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Units of Measurement -->
        <div class="col-11">
          <mat-label class="mat-label">Units of Measurement</mat-label>
          <div class="radio-button-container">
            <mat-radio-group formControlName="isMetricUnit">
              <mat-radio-button value="Imperial">Imperial</mat-radio-button>
              <mat-radio-button value="Metric">Metric</mat-radio-button>
            </mat-radio-group>
          </div>
        </div>

        <!-- Buttons -->
        <div class="button-wrapper">
          <mat-checkbox
            class="show-points-checkbox"
            [(ngModel)]="showPoints"
            [ngModelOptions]="{ standalone: true }">
            Show Points
          </mat-checkbox>
          <button mat-raised-button [disabled]="previewDisabled" (click)="preview()">DOWNLOAD WORD</button>
          <button mat-raised-button [disabled]="previewPdfDisabled" (click)="previewPdf()">PREVIEW PDF</button>
          <button mat-raised-button color="primary" (click)="save()" [disabled]="!form.dirty">SAVE</button>
        </div>
      </div>
    </form>
  </div>

  <!-- ═══════════ Figures Card ═══════════ -->
  <div class="edit-report-wrapper mat-elevation-z1">
    <h1>Figures</h1>
    @if (previewData) {
      <p>Date Modified: {{ formatDate(previewData.updatedAt) }}</p>
    }
    <div class="e-button-wrapper">
      <button mat-raised-button color="primary" (click)="editFigures()">EDIT</button>
    </div>
  </div>

  <!-- ═══════════ Attachments Card ═══════════ -->
  <div class="edit-report-wrapper mat-elevation-z1">
    <h1>Attachments</h1>
    <div class="e-button-wrapper">
      <button
        mat-raised-button
        color="warn"
        class="delete-selected-button"
        [disabled]="!checkedAttachments.size || attachmentsSaving"
        (click)="deleteSelectedAttachments()">
        DELETE SELECTED
      </button>
      <button mat-raised-button color="primary" [disabled]="attachmentsSaving" (click)="AddAttachments()">ADD</button>
      <input
        #attachmentFileInput
        type="file"
        multiple
        [accept]="allowedAttachmentFormat"
        style="display: none"
        (change)="onAttachmentsSelected($event)" />
      </div>
      @if (selectedAttachments.length) {
        <div
          class="attachment-list"
          cdkDropList
          (cdkDropListDropped)="dropAttachment($event)">
          <div class="attachment-header">
            <mat-checkbox
              color="primary"
              class="attachment-checkbox"
              [checked]="allAttachmentsChecked"
              [indeterminate]="someAttachmentsChecked"
              (change)="toggleAllAttachments($event.checked)"
              aria-label="Select all attachments">
              Select all
            </mat-checkbox>
          </div>
          @for (file of selectedAttachments; track file; let i = $index) {
            <div class="attachment-item" cdkDrag>
              <mat-checkbox
                color="primary"
                class="attachment-checkbox"
                [checked]="checkedAttachments.has(file)"
                (change)="toggleAttachmentChecked(file, $event.checked)"
                [attr.aria-label]="'Select ' + file">
              </mat-checkbox>
              <mat-icon class="attachment-drag-handle" cdkDragHandle>drag_indicator</mat-icon>
              <div class="attachment-details">
                <span class="attachment-name">{{ file }}</span>
              </div>
              <button mat-icon-button type="button" aria-label="Remove attachment" (click)="removeAttachment(i)">
                <mat-icon>delete_outline</mat-icon>
              </button>
            </div>
          }
        </div>
      }
    </div>